import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

/**
 * Helper function to safely convert various date formats to ISO string format (YYYY-MM-DD)
 * Used for DateField components that expect ISO string format
 * Handles: Date objects, ISO strings, various date formats, and invalid dates
 *
 * @param {Date|string|null|undefined} dateValue - The date value to format
 * @returns {string} - ISO string (YYYY-MM-DD) or empty string
 */
export const formatDateValue = (dateValue) => {
  // console.log("formatDateValue called with:", dateValue); // Debug log to trace input
  if (!dateValue) return "";

  try {
    let parsed;

    // If it's a Date object, check if it's valid
    if (dateValue instanceof Date) {
      if (!isNaN(dateValue.getTime())) {
        const formatted = dateValue.toISOString().split("T")[0];
        // console.log(
        //   "formatDateValue - Date object to ISO:",
        //   dateValue,
        //   "->",
        //   formatted,
        // );
        return formatted;
      }
      return "";
    }

    // If it's a string
    if (typeof dateValue === "string") {
      // Return empty for clearly invalid/incomplete date strings
      const lowerValue = dateValue.toLowerCase().trim();
      if (
        lowerValue === "tbc" ||
        lowerValue === "tba" ||
        lowerValue === "pending"
      ) {
        console.log("formatDateValue - Invalid date indicator:", dateValue);
        return "";
      }

      // Try to parse as ISO format first (YYYY-MM-DD or full ISO)
      if (dateValue.match(/^\d{4}-\d{2}-\d{2}/)) {
        console.log("formatDateValue - Already ISO format:", dateValue);
        return dateValue.substring(0, 10); // Extract YYYY-MM-DD part
      }

      // Try various date formats
      const formats = [
        "YYYY-MM-DDTHH:mm:ss.SSSZ", // Full ISO with Z
        "YYYY-MM-DDTHH:mm:ss.SSS", // ISO with milliseconds
        "YYYY-MM-DDTHH:mm:ss", // ISO datetime
        "DD/MM/YYYY", // UK format
        "DD/MM/YY", // UK format short
        "DD-MM-YYYY", // Hyphen format
        "DD-MM-YY", // Hyphen short
        "MM/DD/YYYY", // US format
        "YYYY/MM/DD", // Alternative format
        "D/M/YYYY", // Single digit variant
        "D/M/YY", // Single digit variant short
      ];

      for (const format of formats) {
        parsed = dayjs(dateValue, format, true);
        if (parsed.isValid()) {
          const formatted = parsed.format("YYYY-MM-DD");
          console.log(
            "formatDateValue - String parsed with format",
            format,
            ":",
            dateValue,
            "->",
            formatted,
          );
          return formatted;
        }
      }

      // Try generic parsing as last resort
      parsed = dayjs(dateValue);
      if (parsed.isValid()) {
        const formatted = parsed.format("YYYY-MM-DD");
        console.log(
          "formatDateValue - String parsed generically:",
          dateValue,
          "->",
          formatted,
        );
        return formatted;
      }

      // If it couldn't be parsed, return empty
      console.log("formatDateValue - Could not parse string:", dateValue);
      return "";
    }

    console.log("formatDateValue - No value or invalid type:", dateValue);
    return "";
  } catch (error) {
    console.error("formatDateValue - Error formatting date:", error, dateValue);
    return "";
  }
};
